import {type AxiosPromise} from 'axios';
import {createActions, handleActions, type ActionType} from 'redux-actions';
import FormData from 'form-data';

import {ENDPOINTS} from 'constants/endpoints';
import {instanceGusaBackend} from 'libs/api';

import type {TryOnPhotoType, TryOnPupilType} from './photoType';

export const defaultState: TryOnPhotoType = {
    base64: '',
    croppedImage: '',
    pupils: {
        left: {x: 90, y: 105},
        right: {x: 174, y: 105},
        isSaved: false,
    },
};

export const {
    tryOn: {
        setPhotoData,
        setCroppedImage,
        setPupilsCoord,
        toggleSavedStatus,
        handlerUploadCroppedImage,
        uploadCroppedImage,
    },
} = createActions({
    TRY_ON: {
        /**
         * Set photo data
         * @param {string} base64 - Base64 of uploaded image
         * @return {{base64: string}} Action data
         */
        SET_PHOTO_DATA: (base64: string): {base64: string} => ({base64}),

        /**
         * Set cropped data
         * @param {string} base64 - Base64 of cropped image
         * @return {{croppedImage: string}} Action data
         */
        SET_CROPPED_IMAGE: (base64: string): {croppedImage: string} => ({croppedImage: base64}),

        /**
         * Set pupils coordinates
         * @param {'left' | 'right'} type - Type of pupil
         * @param {TryOnPupilType} coords - Coordinates of pupil
         * @return {{left: TryOnPupilType, right: TryOnPupilType}} Action data
         */
        SET_PUPILS_COORD: (
            type: 'left' | 'right',
            coords: TryOnPupilType,
        ): {left?: TryOnPupilType, right?: TryOnPupilType} => ({
            [type]: coords,
        }),

        /**
         * Toggle status for "PD is saved"
         * @param {boolean} isSaved - New status for "PD is saved"
         * @return {{isSaved: boolean}} Action data
         */
        TOGGLE_SAVED_STATUS: (isSaved: boolean): {isSaved: boolean} => ({isSaved}),

        /**
         * Handler "Upload cropped image" for saga
         */
        HANDLER_UPLOAD_CROPPED_IMAGE: () => {},

        /**
         * Upload cropped image
         * @param {string} croppedImage - Base64 of cropped image
         * @return {AxiosPromise} Axios promise
         */
        UPLOAD_CROPPED_IMAGE: (croppedImage: string): AxiosPromise => {
            const data = new FormData();
            const SEND_BY_BASE64 = 2;

            data.append('image', croppedImage);
            data.append('send', SEND_BY_BASE64);

            return instanceGusaBackend({
                headers: {'Content-Type': 'multipart/form-data'},
                method: 'post',
                url: ENDPOINTS.TRYON_UPLOAD_IMAGE,
                data,
            });
        },
    },
});

export const photo = handleActions(
    {
        /**
         * Save photo data in store
         * @param {TryOnPhotoType} state - Current state
         * @param {ActionType<setPhotoData>} action - Dispatched action
         * @return {TryOnPhotoType} New state
         */
        [setPhotoData]: (
            state: TryOnPhotoType,
            {payload}: ActionType<typeof setPhotoData>,
        ): TryOnPhotoType => ({
            ...state,
            base64: payload.base64,
            croppedImage: '',
            pupils: {...defaultState.pupils},
        }),

        /**
         * Save cropped photo in store
         * @param {TryOnPhotoType} state - Current state
         * @param {ActionType<setCroppedImage>} action - Dispatched action
         * @return {TryOnPhotoType} New state
         */
        [setCroppedImage]: (
            state: TryOnPhotoType,
            {payload}: ActionType<typeof setCroppedImage>,
        ): TryOnPhotoType => ({...state, croppedImage: payload.croppedImage}),

        /**
         * Save pupils coordinates in store
         * @param {TryOnPhotoType} state - Current state
         * @param {ActionType<setPupilsCoord>} action - Dispatched action
         * @return {TryOnPhotoType} New state
         */
        [setPupilsCoord]: (
            state: TryOnPhotoType,
            {payload}: ActionType<typeof setPupilsCoord>,
        ): TryOnPhotoType => ({...state, pupils: {...state.pupils, ...payload}}),

        /**
         * Toggle status for "PD is saved" is store
         * @param {TryOnPhotoType} state - Current state
         * @param {ActionType<toggleSavedStatus>} action - Dispatched action
         * @return {TryOnPhotoType} New state
         */
        [toggleSavedStatus]: (
            state: TryOnPhotoType,
            {payload}: ActionType<typeof toggleSavedStatus>,
        ): TryOnPhotoType => ({...state, pupils: {...state.pupils, isSaved: payload.isSaved}}),
    },
    defaultState,
);
